import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus,
  AlertCircle, Clock, User, RefreshCw, X, Check,
  Video, Phone, MapPin, Instagram, Linkedin, Hash,
  Edit2, Trash2, Bell, Image
} from 'lucide-react';
import AdminNotifications from '../components/AdminNotifications';
import AdminThemeToggle from '../components/AdminThemeToggle';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform icons/colors
const PLATFORMS = {
  instagram: { icon: Instagram, color: '#E1306C', label: 'Instagram' },
  linkedin: { icon: Linkedin, color: '#0077B5', label: 'LinkedIn' },
  tiktok: { icon: Hash, color: '#000000', label: 'TikTok' },
  all: { icon: Image, color: '#8B5CF6', label: 'All Platforms' }
};

const AdminCalendar = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('all'); // all, appointments, content
  const [bookings, setBookings] = useState([]);
  const [contentPosts, setContentPosts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [stats, setStats] = useState({ today: 0, this_week: 0, pending_confirmation: 0 });
  const [contentStats, setContentStats] = useState({ scheduled: 0, this_week: 0, posted: 0 });
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false, email: null });
  const [connectingCalendar, setConnectingCalendar] = useState(false);

  // Check for OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      fetchGoogleCalendarStatus();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('error')) {
      setError(`Google Calendar connection failed: ${params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchGoogleCalendarStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/oauth/calendar/status`);
      if (response.ok) {
        const data = await response.json();
        setGoogleCalendarStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch Google Calendar status:', err);
    }
  };

  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const response = await fetch(`${API_URL}/api/oauth/calendar/login`);
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      console.error('Failed to start Google OAuth:', err);
      setError('Failed to connect Google Calendar');
    }
    setConnectingCalendar(false);
  };

  const disconnectGoogleCalendar = async () => {
    try {
      const response = await fetch(`${API_URL}/api/oauth/calendar/disconnect`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setGoogleCalendarStatus({ connected: false, email: null });
      }
    } catch (err) {
      console.error('Failed to disconnect Google Calendar:', err);
    }
  };

  const fetchBookings = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      const params = new URLSearchParams({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      const response = await fetch(`${API_URL}/api/admin/calendar/bookings?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch bookings');
      
      const data = await response.json();
      setBookings(data.bookings || []);
    } catch (err) {
      console.error('Fetch bookings error:', err);
    }
  }, [token, currentDate]);

  const fetchContentPosts = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      const params = new URLSearchParams({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      const response = await fetch(`${API_URL}/api/admin/content-schedule?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setContentPosts(data.posts || []);
        setContentStats(data.stats || { scheduled: 0, this_week: 0, posted: 0 });
      }
    } catch (err) {
      console.error('Fetch content posts error:', err);
      // Use empty array if endpoint doesn't exist yet
      setContentPosts([]);
    }
  }, [token, currentDate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/calendar/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBookings(), fetchContentPosts(), fetchStats(), fetchGoogleCalendarStatus()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBookings, fetchContentPosts, fetchStats]);

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayBookings = bookings.filter(b => {
        const bookingDate = new Date(b.scheduled_date);
        return bookingDate.toDateString() === date.toDateString();
      });
      const dayContent = contentPosts.filter(p => {
        const postDate = new Date(p.scheduled_date);
        return postDate.toDateString() === date.toDateString();
      });
      days.push({ day, date, bookings: dayBookings, contentPosts: dayContent });
    }
    
    return days;
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.date) {
      setSelectedDate(dayInfo);
    }
  };

  const handleBookingClick = (booking, e) => {
    e.stopPropagation();
    setSelectedBooking(booking);
  };

  const handlePostClick = (post, e) => {
    e.stopPropagation();
    setSelectedPost(post);
  };

  const markPostAsPosted = async (postId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/content-schedule/${postId}/mark-posted`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchContentPosts();
        setSelectedPost(null);
      }
    } catch (err) {
      console.error('Mark posted error:', err);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this scheduled post?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/content-schedule/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchContentPosts();
        setSelectedPost(null);
      }
    } catch (err) {
      console.error('Delete post error:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      case 'completed': return '#6b7280';
      default: return '#3b82f6';
    }
  };

  const formatPackage = (pkg) => {
    const names = {
      'diagnostic-review': 'Diagnostic Review',
      'advocacy-launch': 'Advocacy Launch',
      'deep-dive': 'Deep Dive'
    };
    return names[pkg] || pkg || 'Consultation';
  };

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')} className="admin-btn primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const days = getDaysInMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Filter what to show based on active tab
  const shouldShowBookings = activeTab === 'all' || activeTab === 'appointments';
  const shouldShowContent = activeTab === 'all' || activeTab === 'content';

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Calendar & Content Schedule</h1>
            <p className="admin-subtitle">Manage appointments and social media content</p>
          </div>
          <div className="admin-header-actions">
            <AdminThemeToggle />
            <AdminNotifications />
            <button onClick={() => setShowAddContentModal(true)} className="admin-btn secondary">
              <Instagram size={16} />
              Schedule Post
            </button>
            <button onClick={() => setShowAddModal(true)} className="admin-btn primary">
              <Plus size={16} />
              Add Booking
            </button>
          </div>
        </div>

      {/* Tab Navigation */}
      <div className="calendar-tabs">
        <button 
          className={`cal-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Events
        </button>
        <button 
          className={`cal-tab ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          <User size={16} />
          Appointments
        </button>
        <button 
          className={`cal-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <Instagram size={16} />
          Content
        </button>
      </div>

      {/* Stats Cards */}
      <div className="calendar-stats">
        {shouldShowBookings && (
          <>
            <div className="cal-stat-card">
              <CalendarIcon size={20} />
              <div>
                <span className="cal-stat-value">{stats.today}</span>
                <span className="cal-stat-label">Appts Today</span>
              </div>
            </div>
            <div className="cal-stat-card">
              <Clock size={20} />
              <div>
                <span className="cal-stat-value">{stats.this_week}</span>
                <span className="cal-stat-label">This Week</span>
              </div>
            </div>
          </>
        )}
        {shouldShowContent && (
          <>
            <div className="cal-stat-card content">
              <Instagram size={20} />
              <div>
                <span className="cal-stat-value">{contentStats.scheduled}</span>
                <span className="cal-stat-label">Scheduled Posts</span>
              </div>
            </div>
            <div className="cal-stat-card posted">
              <Check size={20} />
              <div>
                <span className="cal-stat-value">{contentStats.posted}</span>
                <span className="cal-stat-label">Posted</span>
              </div>
            </div>
          </>
        )}
        {shouldShowBookings && (
          <div className="cal-stat-card pending">
            <AlertCircle size={20} />
            <div>
              <span className="cal-stat-value">{stats.pending_confirmation}</span>
              <span className="cal-stat-label">Pending</span>
            </div>
          </div>
        )}
      </div>

      {/* Google Calendar Connection Banner */}
      {shouldShowBookings && (
        <div className={`google-calendar-banner ${googleCalendarStatus.connected ? 'connected' : ''}`}>
          <div className="gcal-banner-content">
            <Video size={20} />
            {googleCalendarStatus.connected ? (
              <>
                <span>Google Calendar connected ({googleCalendarStatus.email})</span>
                <span className="gcal-badge">Meet links auto-generated</span>
              </>
            ) : (
              <span>Connect Google Calendar to auto-generate Meet links for appointments</span>
            )}
          </div>
          <div className="gcal-banner-actions">
            {googleCalendarStatus.connected ? (
              <button 
                onClick={disconnectGoogleCalendar} 
                className="gcal-btn disconnect"
              >
                Disconnect
              </button>
            ) : (
              <button 
                onClick={connectGoogleCalendar} 
                className="gcal-btn connect"
                disabled={connectingCalendar}
              >
                {connectingCalendar ? 'Connecting...' : 'Connect Google Calendar'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="calendar-nav">
        <button onClick={() => navigateMonth(-1)} className="nav-btn">
          <ChevronLeft size={20} />
        </button>
        <h2 className="calendar-month">{monthName}</h2>
        <button onClick={() => navigateMonth(1)} className="nav-btn">
          <ChevronRight size={20} />
        </button>
        <button 
          onClick={() => setCurrentDate(new Date())} 
          className="today-btn"
        >
          Today
        </button>
        <button onClick={() => { fetchBookings(); fetchContentPosts(); }} className="refresh-btn">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Calendar Legend */}
      <div className="calendar-legend">
        {shouldShowBookings && (
          <>
            <span className="legend-item"><span className="legend-dot" style={{background: '#10b981'}}></span> Confirmed</span>
            <span className="legend-item"><span className="legend-dot" style={{background: '#f59e0b'}}></span> Pending</span>
          </>
        )}
        {shouldShowContent && (
          <>
            <span className="legend-item"><span className="legend-dot" style={{background: '#E1306C'}}></span> Instagram</span>
            <span className="legend-item"><span className="legend-dot" style={{background: '#0077B5'}}></span> LinkedIn</span>
            <span className="legend-item"><span className="legend-dot" style={{background: '#8B5CF6'}}></span> Content</span>
          </>
        )}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading calendar...</p>
        </div>
      ) : (
        <div className="calendar-container">
          <div className="calendar-header-row">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-header-cell">{day}</div>
            ))}
          </div>
          
          <div className="calendar-grid">
            {days.map((dayInfo, idx) => {
              const isToday = dayInfo.date?.toDateString() === new Date().toDateString();
              const hasBookings = shouldShowBookings && dayInfo.bookings?.length > 0;
              const hasContent = shouldShowContent && dayInfo.contentPosts?.length > 0;
              
              return (
                <div 
                  key={idx}
                  className={`calendar-cell ${!dayInfo.day ? 'empty' : ''} ${isToday ? 'today' : ''} ${hasBookings || hasContent ? 'has-bookings' : ''}`}
                  onClick={() => handleDayClick(dayInfo)}
                >
                  {dayInfo.day && (
                    <>
                      <span className="day-number">{dayInfo.day}</span>
                      <div className="day-bookings">
                        {/* Show bookings */}
                        {shouldShowBookings && dayInfo.bookings?.slice(0, 2).map(booking => (
                          <div 
                            key={booking.id}
                            className="booking-dot"
                            style={{ backgroundColor: getStatusColor(booking.status) }}
                            onClick={(e) => handleBookingClick(booking, e)}
                            title={`${booking.patient_name} - ${formatPackage(booking.service_type)}`}
                          >
                            <span className="booking-time">
                              {new Date(booking.scheduled_date).toLocaleTimeString('en-US', { 
                                hour: 'numeric', 
                                minute: '2-digit' 
                              })}
                            </span>
                          </div>
                        ))}
                        {/* Show content posts */}
                        {shouldShowContent && dayInfo.contentPosts?.slice(0, 2).map(post => {
                          const platform = PLATFORMS[post.platform] || PLATFORMS.all;
                          return (
                            <div 
                              key={post.id}
                              className={`content-dot ${post.status === 'posted' ? 'posted' : ''}`}
                              style={{ backgroundColor: platform.color }}
                              onClick={(e) => handlePostClick(post, e)}
                              title={`${platform.label}: ${post.title}`}
                            >
                              <platform.icon size={10} />
                            </div>
                          );
                        })}
                        {/* Show more indicator */}
                        {((shouldShowBookings && dayInfo.bookings?.length > 2) || 
                          (shouldShowContent && dayInfo.contentPosts?.length > 2)) && (
                          <span className="more-bookings">
                            +{(dayInfo.bookings?.length || 0) + (dayInfo.contentPosts?.length || 0) - 4} more
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day Detail Modal */}
      {selectedDate && (
        <div className="modal-overlay" onClick={() => setSelectedDate(null)}>
          <div className="modal-content day-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedDate.date.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <button onClick={() => setSelectedDate(null)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Appointments Section */}
              {shouldShowBookings && selectedDate.bookings?.length > 0 && (
                <div className="day-section">
                  <h4><User size={16} /> Appointments</h4>
                  <div className="day-bookings-list">
                    {selectedDate.bookings.map(booking => (
                      <div key={booking.id} className="booking-card" onClick={() => setSelectedBooking(booking)}>
                        <div className="booking-time-col">
                          <Clock size={16} />
                          <span>
                            {new Date(booking.scheduled_date).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>
                        <div className="booking-details">
                          <span className="booking-patient">{booking.patient_name}</span>
                          <span className="booking-service">{formatPackage(booking.service_type)}</span>
                        </div>
                        <span 
                          className="booking-status"
                          style={{ color: getStatusColor(booking.status) }}
                        >
                          {booking.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Content Section */}
              {shouldShowContent && selectedDate.contentPosts?.length > 0 && (
                <div className="day-section">
                  <h4><Instagram size={16} /> Scheduled Content</h4>
                  <div className="day-content-list">
                    {selectedDate.contentPosts.map(post => {
                      const platform = PLATFORMS[post.platform] || PLATFORMS.all;
                      return (
                        <div key={post.id} className="content-card" onClick={() => setSelectedPost(post)}>
                          <div className="content-platform" style={{ backgroundColor: platform.color }}>
                            <platform.icon size={16} />
                          </div>
                          <div className="content-details">
                            <span className="content-title">{post.title}</span>
                            <span className="content-type">{post.post_type}</span>
                          </div>
                          <span className={`content-status ${post.status}`}>
                            {post.status === 'posted' ? '✓ Posted' : 'Scheduled'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No events */}
              {(!selectedDate.bookings?.length && !selectedDate.contentPosts?.length) && (
                <div className="no-bookings">
                  <CalendarIcon size={32} />
                  <p>No events on this day</p>
                  <div className="quick-add-btns">
                    <button 
                      className="admin-btn secondary small"
                      onClick={() => { setSelectedDate(null); setShowAddContentModal(true); }}
                    >
                      <Instagram size={14} /> Schedule Post
                    </button>
                    <button 
                      className="admin-btn primary small"
                      onClick={() => { setSelectedDate(null); setShowAddModal(true); }}
                    >
                      <Plus size={14} /> Add Booking
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="modal-content booking-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Details</h3>
              <button onClick={() => setSelectedBooking(null)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="booking-detail-row">
                <User size={18} />
                <div>
                  <label>Patient</label>
                  <p>{selectedBooking.patient_name}</p>
                  <span className="email">{selectedBooking.patient_email}</span>
                </div>
              </div>
              
              <div className="booking-detail-row">
                <CalendarIcon size={18} />
                <div>
                  <label>Date & Time</label>
                  <p>
                    {new Date(selectedBooking.scheduled_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  <span>
                    {new Date(selectedBooking.scheduled_date).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                    {' '}({selectedBooking.duration_minutes || 60} min)
                  </span>
                </div>
              </div>
              
              <div className="booking-detail-row">
                <Clock size={18} />
                <div>
                  <label>Service</label>
                  <p>{formatPackage(selectedBooking.service_type)}</p>
                </div>
              </div>
              
              {selectedBooking.meeting_link && (
                <div className="booking-detail-row meeting-link-row">
                  <Video size={18} />
                  <div>
                    <label>Meeting Link</label>
                    <a 
                      href={selectedBooking.meeting_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="meeting-link"
                    >
                      {selectedBooking.meeting_link}
                    </a>
                  </div>
                </div>
              )}
              
              <div className="booking-status-badge" style={{ backgroundColor: getStatusColor(selectedBooking.status) }}>
                {selectedBooking.status}
              </div>
              
              {selectedBooking.notes && (
                <div className="booking-notes">
                  <label>Notes</label>
                  <p>{selectedBooking.notes}</p>
                </div>
              )}
              
              {selectedBooking.intake_id && (
                <button 
                  className="admin-btn secondary full-width"
                  onClick={() => navigate(`/admin/intakes/${selectedBooking.intake_id}`)}
                >
                  View Intake Form
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Post Detail Modal */}
      {selectedPost && (
        <div className="modal-overlay" onClick={() => setSelectedPost(null)}>
          <div className="modal-content content-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Content Details</h3>
              <button onClick={() => setSelectedPost(null)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="content-detail-platform">
                {(() => {
                  const platform = PLATFORMS[selectedPost.platform] || PLATFORMS.all;
                  return (
                    <span className="platform-badge" style={{ backgroundColor: platform.color }}>
                      <platform.icon size={16} /> {platform.label}
                    </span>
                  );
                })()}
                <span className={`status-badge ${selectedPost.status}`}>
                  {selectedPost.status === 'posted' ? '✓ Posted' : 'Scheduled'}
                </span>
              </div>
              
              <div className="content-detail-row">
                <label>Title</label>
                <p className="content-title-text">{selectedPost.title}</p>
              </div>
              
              <div className="content-detail-row">
                <label>Post Type</label>
                <p>{selectedPost.post_type}</p>
              </div>
              
              <div className="content-detail-row">
                <label>Scheduled Date</label>
                <p>
                  {new Date(selectedPost.scheduled_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  {selectedPost.scheduled_time && ` at ${selectedPost.scheduled_time}`}
                </p>
              </div>
              
              {selectedPost.caption && (
                <div className="content-detail-row">
                  <label>Caption</label>
                  <p className="caption-text">{selectedPost.caption}</p>
                </div>
              )}
              
              {selectedPost.hashtags && (
                <div className="content-detail-row">
                  <label>Hashtags</label>
                  <p className="hashtags-text">{selectedPost.hashtags}</p>
                </div>
              )}
              
              {selectedPost.image_url && (
                <div className="content-detail-row">
                  <label>Image</label>
                  <img src={selectedPost.image_url} alt="Post preview" className="post-preview-image" />
                </div>
              )}
              
              <div className="modal-footer">
                {selectedPost.status !== 'posted' && (
                  <>
                    <button 
                      className="admin-btn secondary"
                      onClick={() => deletePost(selectedPost.id)}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                    <button 
                      className="admin-btn primary"
                      onClick={() => markPostAsPosted(selectedPost.id)}
                    >
                      <Check size={16} /> Mark as Posted
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Booking Modal */}
      {showAddModal && (
        <AddBookingModal 
          token={token}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchBookings();
          }}
        />
      )}

      {/* Add Content Modal */}
      {showAddContentModal && (
        <AddContentModal 
          token={token}
          selectedDate={selectedDate?.date}
          onClose={() => setShowAddContentModal(false)}
          onSuccess={() => {
            setShowAddContentModal(false);
            fetchContentPosts();
          }}
        />
      )}
      </div>
    </div>
  );
};

// Add Booking Modal Component
const AddBookingModal = ({ token, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    service_type: 'diagnostic-review',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    notes: '',
    meeting_link: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_name || !formData.scheduled_date || !formData.scheduled_time) {
      alert('Please fill in required fields');
      return;
    }

    try {
      setSaving(true);
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      
      const response = await fetch(`${API_URL}/api/admin/calendar/bookings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          scheduled_date: scheduledDateTime.toISOString(),
          meeting_link: formData.meeting_link || null
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        throw new Error('Failed to create booking');
      }
    } catch (err) {
      alert('Failed to create booking: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-booking-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add New Booking</h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Patient Name *</label>
            <input
              type="text"
              value={formData.patient_name}
              onChange={e => setFormData({...formData, patient_name: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Patient Email</label>
            <input
              type="email"
              value={formData.patient_email}
              onChange={e => setFormData({...formData, patient_email: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Service Type *</label>
            <select
              value={formData.service_type}
              onChange={e => setFormData({...formData, service_type: e.target.value})}
            >
              <option value="diagnostic-review">Diagnostic Review</option>
              <option value="advocacy-launch">Advocacy Launch</option>
              <option value="deep-dive">Deep Dive</option>
              <option value="follow-up">Follow-up Call</option>
            </select>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={e => setFormData({...formData, scheduled_date: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Time *</label>
              <input
                type="time"
                value={formData.scheduled_time}
                onChange={e => setFormData({...formData, scheduled_time: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Duration (minutes)</label>
            <select
              value={formData.duration_minutes}
              onChange={e => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
            >
              <option value="30">30 minutes</option>
              <option value="60">60 minutes</option>
              <option value="90">90 minutes</option>
              <option value="120">2 hours</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Google Meet Link</label>
            <input
              type="url"
              value={formData.meeting_link}
              onChange={e => setFormData({...formData, meeting_link: e.target.value})}
              placeholder="https://meet.google.com/xxx-xxxx-xxx"
            />
            <small style={{color: '#888', fontSize: '12px', marginTop: '4px', display: 'block'}}>
              Create a Meet in Google Calendar and paste the link here
            </small>
          </div>
          
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              rows={3}
            />
          </div>
          
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="admin-btn secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="admin-btn primary">
              {saving ? 'Creating...' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Content Modal Component
const AddContentModal = ({ token, selectedDate, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    platform: 'instagram',
    post_type: 'carousel',
    scheduled_date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    scheduled_time: '10:00',
    caption: '',
    hashtags: '#CADENCEMedical #PatientAdvocate #SecondOpinion',
    image_url: '',
    reminder: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.scheduled_date) {
      alert('Please fill in required fields');
      return;
    }

    try {
      setSaving(true);
      
      const response = await fetch(`${API_URL}/api/admin/content-schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        throw new Error('Failed to schedule content');
      }
    } catch (err) {
      alert('Failed to schedule content: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-content-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Instagram size={20} /> Schedule Content Post</h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Post Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Week 1 - Carousel: Second Opinions"
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Platform *</label>
              <select
                value={formData.platform}
                onChange={e => setFormData({...formData, platform: e.target.value})}
              >
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="tiktok">TikTok</option>
                <option value="all">All Platforms</option>
              </select>
            </div>
            <div className="form-group">
              <label>Post Type</label>
              <select
                value={formData.post_type}
                onChange={e => setFormData({...formData, post_type: e.target.value})}
              >
                <option value="carousel">Carousel</option>
                <option value="reel">Reel/Video</option>
                <option value="static">Static Image</option>
                <option value="story">Story</option>
                <option value="text">Text Only</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={e => setFormData({...formData, scheduled_date: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input
                type="time"
                value={formData.scheduled_time}
                onChange={e => setFormData({...formData, scheduled_time: e.target.value})}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Caption</label>
            <textarea
              value={formData.caption}
              onChange={e => setFormData({...formData, caption: e.target.value})}
              rows={4}
              placeholder="Write your post caption..."
            />
          </div>
          
          <div className="form-group">
            <label>Hashtags</label>
            <input
              type="text"
              value={formData.hashtags}
              onChange={e => setFormData({...formData, hashtags: e.target.value})}
              placeholder="#CADENCEMedical #PatientAdvocate"
            />
          </div>
          
          <div className="form-group">
            <label>Image URL (optional)</label>
            <input
              type="url"
              value={formData.image_url}
              onChange={e => setFormData({...formData, image_url: e.target.value})}
              placeholder="https://..."
            />
          </div>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.reminder}
                onChange={e => setFormData({...formData, reminder: e.target.checked})}
              />
              <Bell size={16} />
              Set reminder for posting day
            </label>
          </div>
          
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="admin-btn secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="admin-btn primary">
              {saving ? 'Scheduling...' : 'Schedule Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCalendar;
